import { useCallback, useRef } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import type { PlanConfig } from '../types/plan.types'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from '../constants/planIds'
import { EliteTabPlanSwitcherIcon } from '../icons/EliteTabPlanSwitcherIcon'
import ElitePrimeTabPlanSwitcherIcon from '../icons/ElitePrimeTabPlanSwitcherIcon'

type PlanSwitcherProps = {
  plans: PlanConfig[]
  selectedPlanId: string
  onPlanChange: (planId: string) => void
  tabPanelId: string
}

export function PlanSwitcher({ plans, selectedPlanId, onPlanChange, tabPanelId }: PlanSwitcherProps) {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const reduceMotion = useReducedMotion()

  const focusTab = useCallback((planId: string) => {
    tabRefs.current[planId]?.focus()
  }, [])

  const selectAt = useCallback(
    (index: number) => {
      const plan = plans[index]
      if (!plan) return
      onPlanChange(plan.id)
      focusTab(plan.id)
      trackElitePlansClick(ElitePlansEventId.TabSwitch, {
        planId: plan.id,
        tabLabel: plan.tabLabel
      })
    },
    [focusTab, onPlanChange, plans]
  )

  return (
    <div role='tablist' aria-label='Membership plans' className='relative inline-grid auto-cols-fr grid-flow-col items-center'>
      {plans.map((plan, index) => {
        const isSelected = plan.id === selectedPlanId
        const tabId = `elite-plans-tab-${plan.id}`
        return (
          <button
            key={plan.id}
            ref={(el) => {
              tabRefs.current[plan.id] = el
            }}
            type='button'
            role='tab'
            id={tabId}
            aria-label={plan.tabLabel}
            aria-selected={isSelected}
            aria-controls={tabPanelId}
            tabIndex={isSelected ? 0 : -1}
            aria-posinset={index + 1}
            aria-setsize={plans.length}
            className={`ep-motion relative flex min-h-11 min-w-[44px] items-center justify-center border-none bg-transparent px-1 pb-2 pt-1 outline-none transition-opacity duration-200 focus:outline-none focus-visible:outline-none ${isSelected ? 'opacity-100' : 'opacity-70'
              }`}
            onClick={() => selectAt(index)}
          >
            <span aria-hidden='true'>
              <PlanSwitcherTabIcon planId={plan.id} />
            </span>
            <PlanSwitcherTabIndicator isSelected={isSelected} reduceMotion={Boolean(reduceMotion)} />
          </button>
        )
      })}
    </div>
  )
}

export function PlanSwitcherTabIcon({ planId }: { planId: string }) {
  if (planId === ELITE_PLANS_ELITE_PRIME_TAB_ID) {
    return <ElitePrimeTabPlanSwitcherIcon />
  }
  return <EliteTabPlanSwitcherIcon />
}

export function PlanSwitcherTabIndicator({
  isSelected,
  reduceMotion
}: {
  isSelected: boolean
  reduceMotion: boolean
}) {
  if (!isSelected) return null
  if (reduceMotion) {
    return (
      <span
        aria-hidden='true'
        className='ep-motion absolute bottom-0 left-0 right-0 mx-auto h-[3px] w-full rounded-t-full bg-[color:var(--ep-tab-indicator)]'
      />
    )
  }
  return (
    <motion.span
      layoutId='elite-plans-tab-indicator'
      aria-hidden='true'
      className='ep-motion absolute bottom-0 left-0 right-0 mx-auto h-[3px] w-full rounded-t-full bg-[color:var(--ep-tab-indicator)]'
      transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.85 }}
    />
  )
}
